import type { Color } from '~/types/Color'
import type { Design } from '~/types/Design'
import type { PrintType } from '~/types/PrintType'
import { apiURL } from '~/api/common'
import {
  groupColorsByMaterial,
  createPartColorMap,
  getValidColorsForPart,
  isColorValidForPart
} from '~/utils/ai/materialValidation'

const GEMINI_MODEL =
  process.env.REACT_APP_GEMINI_MODEL || 'gemini-2.0-flash-lite'

export interface CustomizationContext {
  availableColors: readonly Color[]
  design: Design
}

export interface ExtendedCustomizationContext extends CustomizationContext {
  selectedColorIds: string[]
  allColors: readonly Color[]
}

export interface AICustomizationSuggestion {
  parts: Record<string, { color: { id: string } }>
  /**
   * ロゴの印刷種別（存在する商品のみ）。
   * 'st' | 'gl' | 'sv' のいずれか。
   */
  printLogo?: { printType: PrintType }
  reasoning: string
  title: string
}

export interface AICustomizationResponse {
  suggestion: AICustomizationSuggestion
}

export async function generateCustomization(
  prompt: string,
  context: CustomizationContext | ExtendedCustomizationContext
): Promise<AICustomizationResponse> {
  // Mock for Storybook
  if (
    typeof window !== 'undefined' &&
    Boolean(
      (window as unknown as Record<string, unknown>).__STORYBOOK_AI_MOCK__
    )
  ) {
    return generateMockResponse(prompt, context)
  }

  // 選択された色の検証（警告ログのみ）
  if ('selectedColorIds' in context && context.selectedColorIds.length > 0) {
    const extendedContext = context as ExtendedCustomizationContext
    const allColors: readonly Color[] = extendedContext.allColors

    for (const colorId of extendedContext.selectedColorIds) {
      const color = allColors.find((c) => c.id === colorId)

      if (!color) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            `Selected color ID ${colorId} not found in available colors`
          )
        }
        continue
      }

      // この色が少なくとも1つのパーツと互換性があるかチェック
      const compatibleParts = context.design.parts.filter((part) =>
        isColorValidForPart(part, color)
      )

      if (compatibleParts.length === 0) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn(
            `Selected color ${colorId} (${color.material.label}) is not compatible with any parts`
          )
        }
      }
    }
  }

  const systemPrompt = createSystemPrompt(context)
  const fullPrompt = `${systemPrompt}

ユーザーのリクエスト: ${prompt}

必ずJSON形式で回答してください。`

  try {
    const res = await fetch(
      `${apiURL}/gemini/${encodeURIComponent(GEMINI_MODEL)}/generate_content`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          contents: [
            {
              role: 'user',
              parts: [{ text: fullPrompt }]
            }
          ],
          generationConfig: {
            temperature: 0.45,
            topP: 0.85,
            maxOutputTokens: 2048,
            responseMimeType: 'application/json'
          }
        })
      }
    )

    if (!res.ok) {
      const errText = await res.text().catch(() => '')
      const error = new Error(
        errText || 'Failed to call Gemini API wrapper'
      ) as (Error & { status?: number }) | Error
      ;(error as Error & { status?: number }).status = res.status
      throw error
    }

    const json = await res.json()

    // Try to extract text from wrapper response
    const text: string = (() => {
      if (typeof json?.text === 'string') return json.text
      const candidate = json?.candidates?.[0]
      const parts =
        candidate?.content?.parts ||
        candidate?.content?.candidates?.[0]?.content?.parts
      if (Array.isArray(parts)) {
        const t = parts
          .map((p: { text?: string }) =>
            typeof p.text === 'string' ? p.text : ''
          )
          .join('')
        if (t) return t
      }
      // Fallback
      return ''
    })()

    // Log cost information if available (dev only)
    if (process.env.NODE_ENV !== 'production' && json?.usageMetadata) {
      const usage = json.usageMetadata as {
        promptTokenCount?: number
        candidatesTokenCount?: number
        totalTokenCount?: number
      }
      console.log('=== Gemini API Usage ===')
      if (usage.promptTokenCount != null)
        console.log('Prompt Tokens:', usage.promptTokenCount)
      if (usage.candidatesTokenCount != null)
        console.log('Completion Tokens:', usage.candidatesTokenCount)
      if (usage.totalTokenCount != null)
        console.log('Total Tokens:', usage.totalTokenCount)

      const inputCost = ((usage.promptTokenCount || 0) / 1000) * 0.00025
      const outputCost = ((usage.candidatesTokenCount || 0) / 1000) * 0.0005
      const totalCost = inputCost + outputCost
      console.log('Estimated Cost:')
      console.log(`  Input: $${inputCost.toFixed(6)}`)
      console.log(`  Output: $${outputCost.toFixed(6)}`)
      console.log(
        `  Total: $${totalCost.toFixed(6)} (約¥${(totalCost * 150).toFixed(2)})`
      )
      console.log('========================')
    }

    if (process.env.NODE_ENV !== 'production') {
      console.log('AI Response:', text)
    }

    // Try to parse as JSON directly first
    let parsed: unknown
    try {
      parsed = JSON.parse(text)
    } catch {
      const jsonMatch = text.match(/```json\n([\s\S]*?)\n```/)
      const jsonText = jsonMatch ? jsonMatch[1] : text
      parsed = JSON.parse(jsonText)
    }

    return validateResponse(
      parsed as unknown as Record<string, unknown>,
      context
    )
  } catch (error) {
    console.error('Failed to generate or parse AI response:', error)
    // Try to detect service unavailability / rate limit conditions
    const status =
      (error as { status?: number })?.status ??
      (error as { statusCode?: number })?.statusCode ??
      (error as { response?: { status?: number } })?.response?.status ??
      (error as { cause?: { status?: number } })?.cause?.status

    const message = String((error as { message?: unknown })?.message ?? '')

    const serialized = (() => {
      try {
        return JSON.stringify(error)
      } catch {
        return ''
      }
    })()

    const text = `${message} ${serialized}`

    const isServiceUnavailable =
      status === 503 ||
      status === 500 ||
      status === 429 ||
      /(?:^|[^a-z])503(?:[^a-z]|$)/i.test(text) ||
      /unavailable|service unavailable|rate limit|quota|resource exhausted|exceeded/i.test(
        text
      )

    if (isServiceUnavailable) {
      const e = new Error('AI_SERVICE_UNAVAILABLE') as Error & {
        status?: number
        code?: string
      }
      e.status = 503
      e.code = 'AI_SERVICE_UNAVAILABLE'
      throw e
    }

    throw new Error('AIの応答を処理できませんでした。もう一度お試しください。')
  }
}

export function createSystemPrompt(
  context: CustomizationContext | ExtendedCustomizationContext
): string {
  // パーツリストに素材情報を追加
  const partsList = context.design.parts
    .map((part) => `- ${part.id}: ${part.name} (素材: ${part.material.label})`)
    .join('\n')

  // 明示的に要求するパーツID一覧（厳密一致させるためJSON配列文字列も作成）
  const partIds = context.design.parts.map((p) => p.id)
  const partIdsJson = `[${partIds.map((id) => `"${id}"`).join(', ')}]`

  // 色を素材ごとにグループ化
  const colorsByMaterial = groupColorsByMaterial(context.availableColors)
  const colorsByMaterialList = Array.from(colorsByMaterial.entries())
    .map(([materialId, colors]) => {
      const materialLabel = colors[0]?.material.label || materialId
      const colorList = colors
        .map((c) => `  - ${c.id}: ${c.name} (${c.colorCode})`)
        .join('\n')
      return `[${materialLabel}]\n${colorList}`
    })
    .join('\n\n')

  // 各パーツに使用可能な色IDのマップを作成
  const partColorMap = createPartColorMap(
    context.design.parts,
    context.availableColors
  )
  const partColorCompatibilityList = Array.from(partColorMap.entries())
    .map(([partId, colors]) => {
      const colorIds = colors.map((c) => `"${c.id}"`).join(', ')
      return `- ${partId}: [${colorIds}]`
    })
    .join('\n')

  // 厳密に許可される色ID配列（文章ではなくJSON配列も併記）
  const allowedColorIdsJson = `[${context.availableColors
    .map((c) => `"${c.id}"`)
    .join(', ')}]`

  const selectedColorIds =
    'selectedColorIds' in context ? context.selectedColorIds : []
  const allColors =
    'allColors' in context ? context.allColors : context.availableColors

  // 選択された色がある場合、各色がどのパーツと互換性があるかを明示
  const selectedColorsInfo =
    selectedColorIds.length > 0
      ? allColors
          .filter((c: Color) => selectedColorIds.includes(c.id))
          .map((c: Color) => {
            const compatibleParts = context.design.parts
              .filter((part) => part.material.id === c.material.id)
              .map((part) => part.id)
            return `- ${c.id}: ${c.name} (${
              c.material.label
            }) - 使用可能なパーツ: [${compatibleParts.join(', ')}]`
          })
          .join('\n')
      : ''

  return `あなたはJOGGOのカスタマイズアシスタントです。
ユーザーの要望に基づいて、革製品のカラーカスタマイズを提案してください。

パーツ構成:
${partsList}

必須パーツID（partsのキーとして使用）:
${partIds.map((id) => `- ${id}`).join('\n')}

利用可能な色（素材別）:
${colorsByMaterialList}

各パーツに使用可能な色:
${partColorCompatibilityList}

許可される色ID（厳密）: ${allowedColorIdsJson}

${
  selectedColorIds.length > 0
    ? `ユーザーが選択した色（これらの色を必ず使用し、適切に配置してください）:
${selectedColorsInfo}`
    : ''
}

重要な制約:
1. 各パーツには必ず色を指定してください
2. 色IDは上記の許可配列 ${allowedColorIdsJson} からのみ選択してください（未知のIDは禁止）
3. **各パーツには、そのパーツの素材と一致する素材を持つ色のみを使用してください**
4. 上記「各パーツに使用可能な色」のリストを参照し、各パーツに対して互換性のある色のみを割り当ててください
${
  selectedColorIds.length > 0
    ? '5. ユーザーが選択した色は必ず1つ以上使用してください（他の色も併用可）。また、合計3色以上になるまで、ユーザが選択した色に合う色を追加で使用してください。選択された色は、互換性のあるパーツにのみ使用してください'
    : '5. 名入れ（printName）は含めないでください - カラーカスタマイズのみを行います'
}
6. titleは10文字以内で、提案の特徴を表してください
7. reasoningは落ち着いた上質な日本語で簡潔に書いてください（誇張表現や絵文字は避ける）
8. partsオブジェクトのキーは次と完全一致させてください: ${partIdsJson}
9. 上記IDは全て必ず含め、余計なキーは一切含めないでください（不足・重複・未知のキーは禁止）
10. partsの各値は必ず { "color": { "id": "<color_id>" } } の形式にしてください
11. 出力はプレーンJSONのみ。説明文・前置き・後置き・マークダウン・バッククォート・コードフェンスは禁止
12. キー名は指定どおり厳密一致。値に null / undefined / 空文字 を使わない
13. ロゴ（printLogo）には一切触れないでください。JSONにも printLogo を含めないでください

デザイン方針（配色理論）:
以下の形式のJSONオブジェクトのみを返してください（マークダウンやコードブロックは使用しないでください）:
{
  "suggestion": {
    "title": "提案のタイトル（簡潔に特徴を表す）",
    "parts": {
      "<part_id>": { "color": { "id": "<color_id>" } }
    },
    "reasoning": "この配色を選んだ理由"
  }
}
`
}

export function validateResponse(
  response: Record<string, unknown>,
  context: CustomizationContext
): AICustomizationResponse {
  const validColorIds = new Set(context.availableColors.map((c) => c.id))
  const requiredPartIds = new Set(context.design.parts.map((p) => p.id))

  // Support legacy shape { suggestions: [ ... ] } by taking the first element
  const rawSuggestion: unknown =
    (response as { suggestion?: unknown }).suggestion ??
    (Array.isArray((response as { suggestions?: unknown })?.suggestions)
      ? (response as { suggestions: unknown[] }).suggestions[0]
      : undefined)

  if (!rawSuggestion || typeof rawSuggestion !== 'object') {
    throw new Error('Invalid response: missing suggestion')
  }

  const suggestion = rawSuggestion as Record<string, unknown>

  const rawPartsMap =
    suggestion.parts && typeof suggestion.parts === 'object'
      ? (suggestion.parts as Record<string, { color?: { id?: string } }>)
      : ({} as Record<string, { color?: { id?: string } }>)

  const normalizedParts: Record<string, { color: { id: string } }> = {}

  for (const partId of Array.from(requiredPartIds)) {
    const part = context.design.parts.find((p) => p.id === partId)

    if (!part) {
      // パーツが見つからない場合（通常発生しない）
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(`Part ${partId} not found in design`)
      }
      continue
    }

    // このパーツに対して有効な色のリストを取得
    const validColors = getValidColorsForPart(part, context.availableColors)

    // 有効な色が1つも存在しない場合のフォールバック処理
    if (validColors.length === 0) {
      const fallbackColor = context.availableColors[0]
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.error(
          `No valid colors found for part ${partId} with material ${part.material.label}, ` +
            `using fallback color ${fallbackColor.id}`
        )
      }
      normalizedParts[partId] = { color: { id: fallbackColor.id } }
      continue
    }

    const assignedColorId = rawPartsMap[partId]?.color?.id

    // 割り当てられた色が存在しない場合
    if (!assignedColorId) {
      normalizedParts[partId] = { color: { id: validColors[0].id } }
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          `No color assigned for part ${partId}, using fallback ${validColors[0].id}`
        )
      }
      continue
    }

    const assignedColor = context.availableColors.find(
      (c) => c.id === assignedColorId
    )

    // 割り当てられた色が存在しない（無効なID）場合
    if (!assignedColor) {
      normalizedParts[partId] = { color: { id: validColors[0].id } }
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          `Invalid color ID ${assignedColorId} for part ${partId}, ` +
            `using fallback ${validColors[0].id}`
        )
      }
      continue
    }

    // 割り当てられた色が存在するが素材が一致しない場合
    if (!isColorValidForPart(part, assignedColor)) {
      normalizedParts[partId] = { color: { id: validColors[0].id } }
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn(
          `Material mismatch: part ${partId} (${part.material.label}) ` +
            `cannot use color ${assignedColorId} (${assignedColor.material.label}), ` +
            `using fallback ${validColors[0].id}`
        )
      }
      continue
    }

    // 有効な色が指定された場合
    normalizedParts[partId] = { color: { id: assignedColorId } }
  }

  const title = suggestion.title
  const finalTitle = !title || typeof title !== 'string' ? 'AI提案' : title

  const reasoning = suggestion.reasoning
  const finalReasoning =
    !reasoning || typeof reasoning !== 'string'
      ? 'AIがカスタマイズを生成しました。'
      : reasoning

  const {
    printName: omitNameUnused,
    printLogo: omitLogoUnused,
    ...rest
  } = suggestion as Record<string, unknown>

  return {
    suggestion: {
      ...(rest as object),
      parts: normalizedParts,
      title: finalTitle,
      reasoning: finalReasoning
    } as AICustomizationSuggestion
  }
}

export function isAIEnabled(): boolean {
  return (
    !!apiURL ||
    (typeof window !== 'undefined' &&
      Boolean(
        (window as unknown as Record<string, unknown>).__STORYBOOK_AI_MOCK__
      ))
  )
}

// Mock response generator for Storybook
export function generateMockResponse(
  prompt: string,
  context: CustomizationContext
): Promise<AICustomizationResponse> {
  // Simulate async delay
  return new Promise((resolve) => {
    setTimeout(() => {
      const availableColors = context.availableColors
      const parts: Record<string, { color: { id: string } }> = {}

      for (let index = 0; index < context.design.parts.length; index++) {
        const part = context.design.parts[index]
        const color =
          availableColors[index % availableColors.length] || availableColors[0]
        parts[part.id] = { color: { id: color.id } }
      }

      resolve({
        suggestion: {
          title: 'AI提案',
          parts,
          reasoning: '選択可能な色からバランスよく配色しました。'
        }
      })
    }, 1500) // Simulate network delay
  })
}
